import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Scale, ChevronDown, ChevronRight, Bell } from "lucide-react";
import { api } from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const OUTCOME_TONES = {
  succeeded: "border-emerald-500/40 text-emerald-400",
  mixed: "border-amber-500/40 text-amber-400",
  failed: "border-wr-red/40 text-wr-red",
  superseded: "border-wr-border text-wr-muted",
  too_early: "border-wr-border text-wr-sub",
};

function JournalField({ label, value, onChange, testid, textarea }) {
  return (
    <div>
      <label className="wr-label">{label}</label>
      {textarea ? (
        <Textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0 text-sm" />
      ) : (
        <Input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0 text-sm" />
      )}
    </div>
  );
}

function DecisionCard({ d, onSaved }) {
  const [open, setOpen] = useState(false);
  const [j, setJ] = useState(d);
  const [busy, setBusy] = useState(false);
  const [oc, setOc] = useState({ actual_outcome: d.actual_outcome || "", outcome_class: d.outcome_class || "succeeded", lessons: d.lessons || "", follow_up: d.follow_up || "" });

  const saveJournal = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/coo/decisions/${d.id}/journal`, {
        context: j.context, rationale: j.rationale, alternatives: j.alternatives,
        assumptions: j.assumptions, expected_outcome: j.expected_outcome,
        success_metric: j.success_metric, target_date: j.target_date, revisit_date: j.revisit_date,
      });
      setJ(data); onSaved && onSaved();
      toast.success("Journal saved.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const saveOutcome = async () => {
    if (!oc.actual_outcome.trim()) { toast.error("Describe the actual outcome."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/coo/decisions/${d.id}/outcome`, oc);
      setJ(data); onSaved && onSaved();
      toast.success("Outcome recorded.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="border border-wr-border bg-wr-surface" data-testid={`decision-${d.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 p-4 text-left" data-testid={`decision-toggle-${d.id}`}>
        <div className="min-w-0">
          <p className="text-sm text-white truncate">{d.title}</p>
          <p className="text-xs text-wr-muted mt-0.5">{d.status} {j.revisit_date ? `· revisit ${String(j.revisit_date).slice(0, 10)}` : ""}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {j.outcome_class && <span className={`wr-mono text-[0.58rem] uppercase px-2 py-1 border ${OUTCOME_TONES[j.outcome_class] || ""}`} data-testid={`decision-outcome-badge-${d.id}`}>{j.outcome_class}</span>}
          {open ? <ChevronDown size={16} className="text-wr-sub" /> : <ChevronRight size={16} className="text-wr-sub" />}
        </div>
      </button>
      {open && (
        <div className="border-t border-wr-border p-4 space-y-3" data-testid={`decision-body-${d.id}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <JournalField label="Context" testid={`dec-context-${d.id}`} value={j.context} onChange={(v) => setJ({ ...j, context: v })} textarea />
            <JournalField label="Rationale" testid={`dec-rationale-${d.id}`} value={j.rationale} onChange={(v) => setJ({ ...j, rationale: v })} textarea />
            <JournalField label="Alternatives considered" testid={`dec-alternatives-${d.id}`} value={j.alternatives} onChange={(v) => setJ({ ...j, alternatives: v })} textarea />
            <JournalField label="Assumptions" testid={`dec-assumptions-${d.id}`} value={j.assumptions} onChange={(v) => setJ({ ...j, assumptions: v })} textarea />
            <JournalField label="Expected outcome" testid={`dec-expected-${d.id}`} value={j.expected_outcome} onChange={(v) => setJ({ ...j, expected_outcome: v })} />
            <JournalField label="Success metric" testid={`dec-metric-${d.id}`} value={j.success_metric} onChange={(v) => setJ({ ...j, success_metric: v })} />
            <JournalField label="Target date (YYYY-MM-DD)" testid={`dec-target-${d.id}`} value={j.target_date} onChange={(v) => setJ({ ...j, target_date: v })} />
            <JournalField label="Revisit date (YYYY-MM-DD)" testid={`dec-revisit-${d.id}`} value={j.revisit_date} onChange={(v) => setJ({ ...j, revisit_date: v })} />
          </div>
          <Button onClick={saveJournal} disabled={busy} data-testid={`dec-save-${d.id}`} className="rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase text-xs h-9">Save journal</Button>

          <div className="border-t border-wr-border pt-3 mt-3">
            <p className="wr-label">Outcome</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              <JournalField label="Actual outcome" testid={`dec-actual-${d.id}`} value={oc.actual_outcome} onChange={(v) => setOc({ ...oc, actual_outcome: v })} textarea />
              <div>
                <label className="wr-label">Classification</label>
                <select data-testid={`dec-class-${d.id}`} value={oc.outcome_class} onChange={(e) => setOc({ ...oc, outcome_class: e.target.value })} className="mt-1 w-full bg-wr-bg border border-wr-border text-white text-sm px-3 py-2 rounded-none focus:outline-none">
                  {["succeeded", "mixed", "failed", "superseded", "too_early"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <JournalField label="Lessons (Sarge treats as your input)" testid={`dec-lessons-${d.id}`} value={oc.lessons} onChange={(v) => setOc({ ...oc, lessons: v })} textarea />
              </div>
            </div>
            <Button onClick={saveOutcome} disabled={busy} data-testid={`dec-outcome-save-${d.id}`} className="mt-2 rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase text-xs h-9">Record outcome</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Decisions() {
  const [decisions, setDecisions] = useState([]);
  const [revisit, setRevisit] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [{ data: all }, { data: due }] = await Promise.all([
        api.get("/board/decisions"),
        api.get("/coo/decisions/revisit"),
      ]);
      setDecisions(Array.isArray(all) ? all : []);
      setRevisit(due);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-6 sm:py-8 pb-28" data-testid="decisions-page">
      <p className="wr-label !text-wr-red">▸ SARGE · DECISIONS</p>
      <h1 className="wr-heading font-bold text-white text-2xl sm:text-3xl mt-2 flex items-center gap-2"><Scale size={24} /> Decision Journal.</h1>
      <p className="text-sm text-wr-sub mt-2">Record context, alternatives, and expected outcome. Sarge surfaces decisions when their revisit date arrives — you record the actual outcome.</p>

      {revisit.length > 0 && (
        <div className="mt-5 border border-amber-500/30 bg-amber-500/5 p-4" data-testid="revisit-queue">
          <p className="wr-label !text-amber-400 flex items-center gap-2"><Bell size={13} /> Due for review ({revisit.length})</p>
          <ul className="mt-2 space-y-1">{revisit.map((d) => <li key={d.id} className="text-sm text-amber-300/90">• {d.title}</li>)}</ul>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {loading && <p className="wr-label" data-testid="decisions-loading">Loading…</p>}
        {!loading && decisions.length === 0 && <p className="text-sm text-wr-muted" data-testid="decisions-empty">No decisions yet. Create them from the Command board or via Sarge.</p>}
        {decisions.map((d) => <DecisionCard key={d.id} d={d} onSaved={load} />)}
      </div>
    </div>
  );
}
